import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { CommonTitle } from '../../../components/items/common.title';
import { Button } from '../../../components/items/Button';
import { useForm } from 'react-hook-form';
import { JoinInput } from '../../../components/items/joinInput';
import { useEffect, useState } from 'react';
import { AlertModal } from '../../../components/modal/alert.modal';
import { zodResolver } from '@hookform/resolvers/zod';
import { companyInfoChangedSchema } from '../../../schema/mypageSchema';
import { fetchCompanyMypage, updateCompanyInfo } from '../../../api/mypage';

interface ChangedCompanyInfo {
  charge: string;
  position: string;
}

interface CompanyState {
  passid: string;
  name: string;
  charge: string;
  position: string;
  phone: string;
  address: string;
  registration: string;
}

const ManagerInfoChange = () => {
  const history = useHistory();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [navigateOnClose, setNavigateOnClose] = useState(false); // 페이지 이동 여부를 추적
  const [companyProfileData, setCompanyProfileData] =
    useState<CompanyState | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<ChangedCompanyInfo>({
    resolver: zodResolver(companyInfoChangedSchema),
  });

  const handleModalClose = () => {
    setIsModalOpen(false);
    if (navigateOnClose) {
      history.push({
        pathname: '/mypage',
      }); // navigateOnClose가 true일 때만 /mypage로 이동
    }
  };

  const onSubmit = async (data: ChangedCompanyInfo) => {
    console.log(data);
    try {
      const response = await updateCompanyInfo(data);

      if (response.statusCode === 200) {
        setModalMessage(response.message);
        setIsModalOpen(true);
        setNavigateOnClose(true);
      } else {
        setModalMessage('회원정보 업데이트에 실패했습니다.');
        setIsModalOpen(true);
        setNavigateOnClose(false);
      }
    } catch (error) {
      console.error('업데이트 오류:', error);
      setModalMessage('회원정보 업데이트 중 오류가 발생했습니다.');
      setIsModalOpen(true);
      setNavigateOnClose(false);
    }
  };

  useEffect(() => {
    const infoFetchData = async () => {
      try {
        const companyInfo = await fetchCompanyMypage();
        setValue('charge', companyInfo.charge);
        setValue('position', companyInfo.position);
      } catch (error) {
        console.error('데이터를 가져오는 중 오류가 발생했습니다:', error);
      }
    };
    infoFetchData();
  }, [setValue]);

  return (
    <Container>
      <CommonTitle title="회사 정보 변경" />
      <form onSubmit={handleSubmit(onSubmit)}>
        <JoinInput
          name="charge"
          label="담당자 이름"
          placeholder="담당자 이름 입력"
          type="text"
          register={register}
          errors={
            errors.charge?.message ? [errors.charge.message as string] : []
          }
        />
        <JoinInput
          name="position"
          label="직책"
          placeholder="직책 입력"
          type="text"
          register={register}
          errors={
            errors.position?.message ? [errors.position.message as string] : []
          }
        />
        <Button title="저 장" type="submit" fiexd />
      </form>

      {isModalOpen && (
        <AlertModal
          handleConfirmClose={handleModalClose}
          confirmText={modalMessage}
          onClick={handleModalClose}
        />
      )}
    </Container>
  );
};

export default ManagerInfoChange;

// style
const Container = styled.div`
  padding: 2.4rem 2rem;
  form {
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
    padding-bottom: 3rem;
    &::-webkit-scrollbar {
      display: none; /* Chrome, Safari, Opera*/
    }
    .select-wrapper {
      padding: 1.1rem 1.1rem 1.6rem 1.5rem;
      background-color: ${(props) => props.theme.gray100};
      border-radius: 0.8rem;
      display: flex;
      flex-direction: column;
      font-size: 1.6rem;
      font-weight: 600;
      span {
        font-size: 1.2rem;
        font-weight: 600;
        color: ${(props) => props.theme.joinLabelColor};
        margin-bottom: 0.6rem;
        line-height: 1.5rem;
      }
      .search-select {
        .react-select__placeholder {
          color: ${(props) => props.theme.placeholderColor};
          font-weight: 400;
        }
        .react-select__control {
          background-color: transparent;
          min-height: 2rem;
          height: 2rem;
          border: none;
          .react-select__value-container {
            padding: 0;
          }
        }
        .react-select__indicator {
          padding: 0;
        }
      }
    }
    .setting-wrap {
      display: flex;
      flex-direction: column;
      gap: 1.2rem;
      height: calc(100% - 16rem);
      .title {
        font-size: 1.5rem;
        font-weight: 600;
        display: flex;
        align-items: flex-start;
        margin-bottom: 0.8rem;
        span {
          color: #ff5151;
          margin-left: 0.2rem;
          transform: translateY(-3px);
        }
      }
      .input-box {
        margin-bottom: 0.7rem;
        input {
          width: 100%;
          border: 1px solid #d6dbe1;
          padding: 1.4rem 1.8rem 1.5rem;
          border-radius: 0.8rem;
        }
      }
      .text-box {
        flex: 1;
        border-radius: 0.8rem;
        border: 1px solid ${(props) => props.theme.searchBoxBorderedColor};
        overflow: hidden;
        padding: 1.3rem 1.8rem 1rem;
        font-size: 1.4rem;
        textarea {
          width: 100%;
          height: 100%;
          flex: 1;
          min-height: 3.6rem;
          padding: 0.1rem;
          border: none;
          font-weight: 400;
          &::placeholder {
            color: ${(props) => props.theme.searchPlaceholderColor};
            font-size: 1.4rem;
            font-weight: 00;
          }
        }
      }
      .on-off-btn {
        display: flex;
        align-items: center;
        justify-content: space-between;
        .title {
          margin-bottom: 0;
        }
      }
    }
  }
`;
